import config from "../config";
import {
  generateKeyPair,
  wrapPrivateKey,
  unwrapPrivateKey,
  encryptForPublicKey,
} from "./OstreonEncryption";

const base_url = config.base_url;

/*
  Partner keys — the browser half of partner sharing.

  A partner holds a keypair; each of their customers wraps their own key with the
  partner's public half. The private half never leaves the browser unwrapped and
  is never stored anywhere Ostreon can read.

  Everything here fails soft. None of it is on the path to logging in or using
  the app, so a failure means sharing is unavailable for now, never a blocked
  login.
*/

const post = (path, body) =>
  fetch(base_url + path, {
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-type": "application/x-www-form-urlencoded",
      credentials: "include",
    },
    credentials: "include",
    body,
  });

/*
  Called once, when a partner sets their own password for the first time.

  Two wraps of one private key: the password-derived secret, and the recovery
  code they are given at the same moment. Two independent ways back to the same
  keypair -- which is what stops a forgotten password taking every customer key
  with it. Ostreon gets neither secret, so it holds no way in at all.
*/
export const createPartnerKeys = async (private_key, recovery_key) => {
  const pair = await generateKeyPair();
  const private_key_pwd = await wrapPrivateKey(private_key, pair.private_key);
  /*
    The recovery wrap is optional, and MUST be omitted rather than faked when
    there is no recovery key. Wrapping under an absent one derives the key from
    the string "undefined" -- a wrap anybody could open, which is worse than
    having no second way in at all.
  */
  const private_key_recovery = recovery_key
    ? await wrapPrivateKey(recovery_key, pair.private_key)
    : undefined;
  const body = { public_key: pair.public_key, private_key_pwd };
  if (private_key_recovery) body.private_key_recovery = private_key_recovery;
  const res = await post(
    "user/partner/keys/create",
    new URLSearchParams(body).toString()
  );
  // 409 means one already exists, which is success as far as the caller cares.
  if (res.status !== 200 && res.status !== 409) {
    throw new Error(`Could not store the partner keypair (${res.status})`);
  }
  return pair.public_key;
};

/*
  The partner's own private key, unwrapped for this session so their browser can
  open the customer keys. Null when they have no keypair yet, or when the
  password does not open it -- both of which mean "sharing unavailable", not an
  error to show.
*/
export const loadPartnerPrivateKey = async (private_key) => {
  try {
    const res = await post("user/partner/keys/mine", "");
    if (res.status !== 200) return null;
    const data = await res.json();
    if (!data?.private_key_pwd) return null;
    const unwrapped = await unwrapPrivateKey(private_key, data.private_key_pwd);
    /*
      A failed decryption does not throw and does not return empty -- decryptText
      answers with the two characters , its sentinel. That is truthy, so
      returning it straight would hand every caller a "key" that is really a
      failure, and the error would surface much later as an unreadable RSA key.

      An unwrap that did not work means the same thing as having no keypair:
      this password does not open it. Usually because it was set before the
      password was last changed.
    */
    if (!unwrapped || unwrapped === '""' || unwrapped === "") return null;
    return unwrapped;
  } catch (error) {
    console.log(error);
    return null;
  }
};

/*
  Re-wrap after the password changes. The keypair is untouched, so no customer
  wrap has to be rebuilt -- this is one small record, not a migration.
*/
export const rewrapPartnerKeys = async (old_private_key, new_private_key) => {
  const unwrapped = await loadPartnerPrivateKey(old_private_key);
  if (!unwrapped) return false;
  const private_key_pwd = await wrapPrivateKey(new_private_key, unwrapped);
  const res = await post(
    "user/partner/keys/rewrap",
    new URLSearchParams({ private_key_pwd }).toString()
  );
  return res.status === 200;
};

/*
  Make sure this partner has a sharing keypair, creating one if they have none.

  The keypair is normally created at /partner/start, the once-only screen where
  a partner sets their first password. A partner who never went through it --
  or whose record was edited by hand -- has none, and every sharing surface is
  dead for them with nothing on screen to say why. This backfills that on any
  sign-in.

  It only ever creates a MISSING keypair. It will not replace one that exists
  but cannot be opened: every customer wrap was made for the current public key,
  and rotating it would orphan all of them at once. That is a deliberate act,
  not something a routine sign-in should do on its own.

  `recovery_key` is the partner's own account recovery key when their browser
  can recover it, which gives the same second way in that /partner/start
  provides. Without one the keypair is wrapped under the password alone -- still
  fully working, but a forgotten password would lose it.
*/
export const ensurePartnerKeys = async (private_key, recovery_key) => {
  try {
    if (!private_key) return false;
    const res = await post("user/partner/keys/mine", "");
    if (res.status !== 200) return false;
    const data = await res.json();
    // Already has one. Whether it opens is not this function's business.
    if (data?.public_key) return false;
    await createPartnerKeys(private_key, recovery_key);
    return true;
  } catch (error) {
    // Sharing stays unavailable until the next sign-in; never a blocked login.
    console.log(error);
    return false;
  }
};

/*
  A customer publishing their key to their partner.

  Runs on login and whenever they share an asset -- the two moments their key is
  in memory. The server decides whether it is needed: no partner, no permission,
  no keypair yet, or a wrap already made for the current public key all answer
  no. Keyed on the public key rather than the partner's id, so a rotated keypair
  is noticed rather than silently unopenable.

  This is also the whole answer to customers who registered before their partner
  activated: nothing could be wrapped then, and this picks it up next time they
  sign in.
*/
export const publishKeyToPartner = async (private_key, force = false) => {
  try {
    if (!private_key) return false;
    /*
       after a password change. The wrap on file is for the key that just
      stopped existing; nothing the server can see says so, so the browser says
      it.
    */
    const statusRes = await post(
      "user/partnerkey/status",
      force ? new URLSearchParams({ force: "true" }).toString() : ""
    );
    if (statusRes.status !== 200) return false;
    const status = await statusRes.json();
    if (!status?.needed || !status?.public_key) return false;

    const wrap = await encryptForPublicKey(status.public_key, private_key);
    const res = await post(
      "user/partnerkey/store",
      new URLSearchParams({ wrap }).toString()
    );
    return res.status === 200;
  } catch (error) {
    // Sharing simply stays unavailable until the next sign-in.
    console.log(error);
    return false;
  }
};
